import React, { useState, useEffect, useCallback, useMemo, memo } from 'react'
import { isMobile } from 'react-device-detect'
import TagManager from 'react-gtm-module'
import { useNavigate } from 'react-router-dom'
import { postLikeProducts } from '../../api/product'
import { useMyContext } from '../../components/provider/MyProvider'
import { AFWebLinkType, handlePostMessage, RNPostMessage, sendSetAppsflyerEvent } from '../../js/postMessage'
import { imgURL } from '../Footer'
import { useAppContext } from '../provider/AppProvider'
import { useCommonContext } from '../provider/CommonProvider'
import { DataLayerArgs } from '../../@types/dataLayerObject'
import { MultiLevelCategory } from '@shopby/shop-sdk'
import { addToWishlist } from '../../js/pixelEvent'
import { getParsedSessionStorageItem } from '../../js/script'

interface LikeBtnProps {
  item?: any
  text?: string
  categoryDepth1?: MultiLevelCategory
  categoryDepth2?: MultiLevelCategory
  categoryDepth3?: MultiLevelCategory
  depthLabel?: string | undefined
  promotion_id?: number
  promotion_name?: string
  idx?: number
}

const LikeBtn: React.FC<LikeBtnProps> = ({
  item,
  text,
  categoryDepth1,
  categoryDepth2,
  categoryDepth3,
  depthLabel,
  promotion_name,
  promotion_id,
  idx,
}) => {
  const [wish, setWish] = useState<boolean>(item.liked)
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { isAppView } = useAppContext()
  const { setIsLogin, hasAccessToken } = useCommonContext()
  const navigate = useNavigate()

  const createGtmWishItem = useCallback((wishData: LikeBtnProps) => {
    const { item, idx, categoryDepth1, categoryDepth2, categoryDepth3, depthLabel, promotion_id, promotion_name } =
      wishData
    return {
      dataLayer: {
        event: 'add_to_wishlist',
        ecommerce: {
          currency: 'KRW',
          value: item?.salePrice - item?.immediateDiscountAmt,
          items: [
            {
              item_id: String(item?.productNo),
              item_name: item?.productName,
              affiliation: '어바웃피싱',
              item_list_price: item?.salePrice,
              item_selling_price: item?.salePrice - item?.immediateDiscountAmt,
              price: item?.salePrice - item?.immediateDiscountAmt,
              item_total_discount: item?.immediateDiscountAmt,
              discount: 0,
              index: idx !== undefined ? idx : 0,
              item_brand: item?.brandName,
              item_category: categoryDepth1?.label,
              item_category2: categoryDepth2?.label,
              item_category3: depthLabel,
              promotion_id: String(promotion_id),
              promotion_name: promotion_name,
              item_variant: '',
              item_list_id: categoryDepth3?.categoryNo ? String(categoryDepth3.categoryNo) : '',
              item_list_name: depthLabel,
            },
          ],
        },
      },
    }
  }, [])

  const wishListEventHandler = useCallback(
    (gtmWishItem: DataLayerArgs) => {
      if (isAppView) {
        sendSetAppsflyerEvent({
          eventName: 'add_to_wishlist',
          eventType: gtmWishItem?.dataLayer?.ecommerce,
        })
      } else {
        TagManager.dataLayer(gtmWishItem)
      }
    },
    [isAppView]
  )

  const likeButtonClickHandler = useCallback(async () => {
    if (hasAccessToken) {
      const likeItem = {
        like: wish ? 'N' : 'Y',
        productNo: item.productNo,
      }

      try {
        const res = await postLikeProducts(likeItem)
        if (res.data.length !== 0) {
          const isLiked = res.data[0].like === 'Y'
          setIsToast(true)
          setToastMessage(isLiked ? '찜 리스트에 저장되었습니다.' : '찜 리스트에 제외되었습니다.')
          setToastBtnMessage('바로가기')
          setToastEvent(() => () => {
            if (isAppView) {
              RNPostMessage(AFWebLinkType.moveToLink, {
                url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/wishList`,
              })
            } else {
              navigate('/wishList')
            }
          })

          const wishData = {
            item,
            idx,
            categoryDepth1,
            categoryDepth2,
            categoryDepth3,
            depthLabel,
            promotion_id,
            promotion_name,
          }
          const gtmWishItem = createGtmWishItem(wishData)
          addToWishlist(item.productNo, item.productName, item.salePrice, item.immediateDiscountAmt)
          wishListEventHandler(gtmWishItem)

          setWish((prev: boolean) => !prev)
        }
      } catch (err) {
        console.log(err)
      }
    } else {
      const app = getParsedSessionStorageItem('app')
      if (app) {
        handlePostMessage({ actionType: 'onSignInInducement' })
        if (isAppView) {
          // onSignInInducement();
          RNPostMessage(AFWebLinkType.onSignInInducement)
        } else {
          setIsToast(true)
          setToastMessage('로그인이 필요한 서비스입니다.')
          setToastBtnMessage('로그인')
          setToastEvent(() => () => setIsLogin(true))
        }
      }
    }
  }, [
    hasAccessToken,
    wish,
    item,
    setIsToast,
    setToastMessage,
    setToastBtnMessage,
    setToastEvent,
    navigate,
    createGtmWishItem,
    wishListEventHandler,
  ])

  useEffect(() => {
    setWish(item.liked)
  }, [item])

  const heartIcon = useMemo(() => {
    const iconSrc = wish ? `${imgURL}/icon_heart_fill.png` : `${imgURL}/icon_heart_outline.png`
    const altText = '찜하기 아이콘'
    return <img src={iconSrc} alt={altText} />
  }, [wish])

  return (
    <button
      className='btn_like'
      onClick={() => {
        likeButtonClickHandler()
      }}
    >
      {heartIcon}
      {text && !isMobile ? text : null}
    </button>
  )
}

export default memo(LikeBtn)
